import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from './firebase';
import { getLocalSiteSettings } from './siteSettingsService';

export interface EmailConfig {
  activeProvider: 'zoho' | 'gmail_oauth' | 'brevo' | 'smtp' | 'emailjs' | 'groq';
  primaryProvider?: 'zoho' | 'gmail_oauth' | 'brevo' | 'smtp';
  fallbackProviders?: ('zoho' | 'gmail_oauth' | 'brevo' | 'smtp')[];
  providerPriority?: ('zoho' | 'gmail_oauth' | 'brevo' | 'smtp')[];
  debugMode?: boolean; // Email Debug / Dry Run Mode

  // Provider Toggles
  zohoEnabled?: boolean;
  gmailEnabled?: boolean;
  brevoEnabled?: boolean;
  smtpEnabled?: boolean;

  // Zoho SMTP Dedicated Panel Configuration
  zohoHost?: string;
  zohoPort?: number;
  zohoEncryption?: 'ssl' | 'tls' | 'none';
  zohoUser?: string;
  zohoPass?: string;
  zohoFromEmail?: string;
  zohoFromName?: string;
  zohoReplyTo?: string;
  zohoTimeout?: number;
  zohoEnableSSL?: boolean;
  zohoEnableTLS?: boolean;

  // Gmail OAuth2 Primary Engine
  gmailClientId?: string;
  gmailClientSecret?: string;
  gmailRefreshToken?: string;
  gmailUserEmail?: string;

  // Generic SMTP (Nodemailer Backup)
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpSecurity?: 'auto' | 'starttls' | 'ssl' | 'none';
  smtpUser: string;
  smtpPass: string;
  fromEmail: string;
  fromName: string;
  replyToEmail?: string;
  smtpTimeout?: number;
  smtpEnableSSL?: boolean;
  smtpEnableTLS?: boolean;

  // Brevo (Sendinblue) API v3 Backup
  brevoApiKey: string;
  brevoSenderEmail: string;
  brevoSenderName: string;

  // EmailJS
  emailJsServiceId: string;
  emailJsTemplateId: string;
  emailJsPublicKey: string;
  emailJsPrivateKey: string;

  // GROQ AI
  groqApiKey: string;

  // Invoice & Email Branding Settings
  businessName?: string;
  businessAddress?: string;
  supportEmail?: string;
  supportPhone?: string;
  invoicePrefix?: string;
  taxRatePercent?: number;
  invoiceTaxRate?: number;
  currencySymbol?: string;
  termsAndConditions?: string;
  invoiceTerms?: string;
  emailLogoUrl?: string;

  // Notification Event Switches
  notifyOnSignUp: boolean;
  notifyOnWelcome?: boolean;
  notifyOnVerifyEmail?: boolean;
  notifyOnOtp?: boolean;
  notifyOnForgotPassword: boolean;
  notifyOnOrderCreated: boolean;
  notifyOnPaymentPending?: boolean;
  notifyOnPaymentSuccessful?: boolean;
  notifyOnPaymentFailed?: boolean;
  notifyOnOrderStatusChanged: boolean;
  notifyOnOrderApproved?: boolean;
  notifyOnOrderRejected?: boolean;
  notifyOnOrderCancelled?: boolean;
  notifyOnOrderRefunded?: boolean;
  notifyOnDownloadReady?: boolean;
  notifyOnCouponNotification?: boolean;
  notifyOnPromotionalCampaign?: boolean;
  notifyOnAdminAlert?: boolean;
  notifyOnCustomerSupportReply?: boolean;
}

export interface EmailSendResult {
  success: boolean;
  message: string;
  providerUsed?: string;
  attempts?: number;
  deliveryTimeMs?: number;
  messageId?: string;
  smtpResponse?: string;
  serverResponse?: string;
  errorDetails?: string;
  debugMode?: boolean;
  debugDetails?: {
    endpoint: string;
    method?: string;
    headers?: Record<string, string>;
    payload: any;
    simulatedStatusCode: number;
    simulatedResponseBody: any;
  };
}

export const DEFAULT_EMAIL_CONFIG: EmailConfig = {
  activeProvider: 'zoho',
  primaryProvider: 'zoho',
  fallbackProviders: ['brevo', 'gmail_oauth', 'smtp'],
  providerPriority: ['zoho', 'brevo', 'gmail_oauth', 'smtp'],
  debugMode: false,
  zohoEnabled: true,
  gmailEnabled: true,
  brevoEnabled: true,
  smtpEnabled: true,

  zohoHost: 'smtp.zoho.com',
  zohoPort: 465,
  zohoEncryption: 'ssl',
  zohoUser: 'support@rheditography.net',
  zohoPass: 'A9TVueQpJ1wR',
  zohoFromEmail: 'support@rheditography.net',
  zohoFromName: 'RH Editography',
  zohoReplyTo: 'support@rheditography.net',
  zohoTimeout: 10000,
  zohoEnableSSL: true,
  zohoEnableTLS: false,

  gmailClientId: '5727301756-rh27iej1lcipb8far6jdu5eunc1p6ej6.apps.googleusercontent.com',
  gmailClientSecret: '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',
  gmailRefreshToken: '1//04ogRJLegTfV8CgYIARAAGAQSNwF-L9IrSdAWc1RRqx0bwVWQIUXKAIOCWniWHJuCVMfMyJwTrQ8ILsm-fux95ZpJ7hgUXmEq908',
  gmailUserEmail: 'admin@rheditography.net',

  smtpHost: 'mail.rheditography.net',
  smtpPort: 587,
  smtpSecure: false,
  smtpSecurity: 'auto',
  smtpUser: 'support@rheditography.net',
  smtpPass: '',
  fromEmail: 'support@rheditography.net',
  fromName: 'RH Editography',
  replyToEmail: 'support@rheditography.net',
  smtpTimeout: 10000,
  smtpEnableSSL: false,
  smtpEnableTLS: true,

  brevoApiKey: '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',
  brevoSenderEmail: 'support@rheditography.net',
  brevoSenderName: 'RH Editography',

  emailJsServiceId: '',
  emailJsTemplateId: '',
  emailJsPublicKey: '',
  emailJsPrivateKey: '',

  groqApiKey: '',

  businessName: 'RH Editography',
  businessAddress: '123 Creative Studio Way, Digital District, NY 10001',
  supportEmail: 'support@rheditography.net',
  supportPhone: '+1 (800) 555-RH-EDIT',
  invoicePrefix: 'INV-2026',
  taxRatePercent: 0,
  currencySymbol: '$',
  termsAndConditions: 'All digital purchases grant a perpetual license for personal and commercial usage according to RH Editography licensing terms. Downloadable digital goods are non-refundable once accessed.',
  emailLogoUrl: '/assets/logo.svg',

  notifyOnSignUp: true,
  notifyOnWelcome: true,
  notifyOnVerifyEmail: true,
  notifyOnOtp: true,
  notifyOnForgotPassword: true,
  notifyOnOrderCreated: true,
  notifyOnPaymentPending: true,
  notifyOnPaymentSuccessful: true,
  notifyOnPaymentFailed: true,
  notifyOnOrderStatusChanged: true,
  notifyOnOrderApproved: true,
  notifyOnOrderRejected: true,
  notifyOnOrderCancelled: true,
  notifyOnOrderRefunded: true,
  notifyOnDownloadReady: true,
  notifyOnCouponNotification: true,
  notifyOnPromotionalCampaign: true,
  notifyOnAdminAlert: true,
  notifyOnCustomerSupportReply: true,
};

const COLLECTION_NAME = 'site_settings';
const DOC_ID = 'email_config';

let cachedEmailConfig: EmailConfig = { ...DEFAULT_EMAIL_CONFIG };

try {
  const saved = typeof window !== 'undefined' ? localStorage.getItem('rh_email_config') : null;
  if (saved) {
    cachedEmailConfig = { ...DEFAULT_EMAIL_CONFIG, ...JSON.parse(saved) };
  }
} catch (e) {
  // Ignore
}

export function getLocalEmailConfig(): EmailConfig {
  const siteSettings = getLocalSiteSettings();
  const siteLogo = '/assets/logo.svg';
  return {
    ...cachedEmailConfig,
    emailLogoUrl: siteLogo
  };
}

export async function fetchEmailConfigFromFirestore(): Promise<EmailConfig> {
  // If user is not yet loaded, wait briefly for auth to initialize
  if (!auth.currentUser) {
    try {
      await new Promise<void>((resolve) => {
        const unsub = onAuthStateChanged(auth, () => {
          unsub();
          resolve();
        });
        setTimeout(resolve, 500);
      });
    } catch {}
  }

  // 1. Try direct read from client Firestore with bounded timeout
  try {
    const docRef = doc(db, COLLECTION_NAME, DOC_ID);
    const snapPromise = getDoc(docRef);
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Firestore getDoc timeout')), 2500)
    );
    const snap = await Promise.race([snapPromise, timeoutPromise]) as any;
    if (snap.exists()) {
      const merged: EmailConfig = { ...DEFAULT_EMAIL_CONFIG, ...snap.data() };
      cachedEmailConfig = merged;
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem('rh_email_config', JSON.stringify(merged));
        }
      } catch (e) {}
      return merged;
    }
  } catch (err) {
    console.warn('Error fetching Email Config from Firestore, falling back to backend admin route:', err);
  }

  // 2. Fallback to server admin endpoint if Firestore permission was pending or rejected
  try {
    const token = await auth.currentUser?.getIdToken();
    const headers: Record<string, string> = {
      'x-admin-access': 'true'
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch('/api/admin/email-config', {
      headers,
      signal: AbortSignal.timeout(3000)
    });
    if (res.ok) {
      const data = await res.json();
      if (data && typeof data === 'object' && Object.keys(data).length > 0) {
        const merged: EmailConfig = { ...DEFAULT_EMAIL_CONFIG, ...data };
        cachedEmailConfig = merged;
        try {
          if (typeof window !== 'undefined') {
            localStorage.setItem('rh_email_config', JSON.stringify(merged));
          }
        } catch (e) {}
        return merged;
      }
    }
  } catch (apiErr) {
    console.warn('Server fallback /api/admin/email-config note:', apiErr);
  }

  return cachedEmailConfig;
}

export function subscribeToEmailConfigFromFirestore(
  callback: (config: EmailConfig) => void
): () => void {
  const docRef = doc(db, COLLECTION_NAME, DOC_ID);
  
  callback(cachedEmailConfig);

  // Background refresh via async fetch (both Firestore and Admin endpoint)
  fetchEmailConfigFromFirestore().then((latest) => {
    if (latest) {
      callback(latest);
    }
  }).catch(() => {});

  return onSnapshot(
    docRef,
    (snap) => {
      if (snap.exists()) {
        const merged: EmailConfig = { ...DEFAULT_EMAIL_CONFIG, ...snap.data() };
        cachedEmailConfig = merged;
        try {
          if (typeof window !== 'undefined') {
            localStorage.setItem('rh_email_config', JSON.stringify(merged));
          }
        } catch (e) {}
        callback(merged);
      } else {
        callback(cachedEmailConfig);
      }
    },
    (err) => {
      console.warn('Email Config snapshot note:', err);
      // Try backend fetch if snapshot encountered permissions
      fetchEmailConfigFromFirestore().then((refreshed) => {
        if (refreshed) callback(refreshed);
      }).catch(() => {
        callback(cachedEmailConfig);
      });
    }
  );
}

export async function saveEmailConfigToFirestore(config: EmailConfig): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, DOC_ID);
  cachedEmailConfig = config;
  
  try {
    if (typeof window !== 'undefined') {
      localStorage.setItem('rh_email_config', JSON.stringify(config));
    }
  } catch (e) {}

  let firestoreSaved = false;
  try {
    const setPromise = setDoc(docRef, config, { merge: true });
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Firestore setDoc timeout')), 4000)
    );
    await Promise.race([setPromise, timeoutPromise]);
    firestoreSaved = true;
  } catch (fsErr) {
    console.warn('Direct Firestore setDoc note, will sync via backend admin API:', fsErr);
  }

  // Always sync to backend admin API so both Firestore (via Admin SDK) and memory are permanently updated
  try {
    const token = await auth.currentUser?.getIdToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-admin-access': 'true'
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch('/api/admin/email-config', {
      method: 'POST',
      headers,
      signal: AbortSignal.timeout(5000),
      body: JSON.stringify(config)
    });
    if (res.ok) {
      firestoreSaved = true;
    }
  } catch(e) {
    console.warn('Failed to sync email config to backend', e);
  }

  if (!firestoreSaved) {
    throw new Error('Failed to save email configuration. Please ensure you are logged in as Admin.');
  }
}

/**
 * Dispatch Email request to Node backend API /api/send-email
 */
export async function sendEmailNotification(payload: {
  to: string;
  subject: string;
  type:
    | 'SIGNUP'
    | 'WELCOME'
    | 'VERIFY_EMAIL'
    | 'OTP_VERIFICATION'
    | 'FORGOT_PASSWORD'
    | 'ORDER_CREATED'
    | 'PAYMENT_PENDING'
    | 'PAYMENT_SUCCESSFUL'
    | 'PAYMENT_FAILED'
    | 'ORDER_STATUS_CHANGED'
    | 'ORDER_APPROVED'
    | 'ORDER_REJECTED'
    | 'ORDER_CANCELLED'
    | 'ORDER_REFUNDED'
    | 'DOWNLOAD_READY'
    | 'COUPON_NOTIFICATION'
    | 'PROMOTIONAL_CAMPAIGN'
    | 'ADMIN_ALERT'
    | 'CUSTOMER_SUPPORT_REPLY'
    | 'CUSTOM'
    | string;
  html?: string;
  text?: string;
  orderData?: any;
  userData?: any;
  configOverride?: EmailConfig;
}): Promise<EmailSendResult> {
  try {
    const siteSettings = getLocalSiteSettings();
    const siteLogo = siteSettings.siteLogoEmailUrl || siteSettings.siteLogoHeaderUrl || '/assets/logo.png';
    const baseConfig = payload.configOverride || cachedEmailConfig || (await fetchEmailConfigFromFirestore());
    const activeConfig = {
      ...baseConfig,
      emailLogoUrl: siteLogo || baseConfig.emailLogoUrl || '/assets/logo.png'
    };

    // Check notification flags
    if (payload.type === 'SIGNUP' && !activeConfig.notifyOnSignUp) {
      return { success: true, message: 'Signup notifications disabled' };
    }
    if (payload.type === 'FORGOT_PASSWORD' && !activeConfig.notifyOnForgotPassword) {
      return { success: true, message: 'Password reset notifications disabled' };
    }
    if (payload.type === 'ORDER_CREATED' && !activeConfig.notifyOnOrderCreated) {
      return { success: true, message: 'Order created notifications disabled' };
    }
    if (payload.type === 'ORDER_STATUS_CHANGED' && !activeConfig.notifyOnOrderStatusChanged) {
      return { success: true, message: 'Order status change notifications disabled' };
    }

    const response = await fetch('/api/send-email', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: AbortSignal.timeout(12000),
      body: JSON.stringify({
        to: payload.to,
        subject: payload.subject,
        type: payload.type,
        html: payload.html,
        text: payload.text,
        orderData: payload.orderData,
        userData: payload.userData,
        config: activeConfig,
      }),
    });

    const responseContentType = response.headers.get('content-type') || '';
    const rawText = await response.text();
    let resData: any = {};
    let isApiSuccess = false;

    if (responseContentType.includes('application/json') || rawText.trim().startsWith('{') || rawText.trim().startsWith('[')) {
      try {
        resData = JSON.parse(rawText);
        if (resData.success) isApiSuccess = true;
      } catch (e) {
        console.error('Failed to parse email endpoint JSON:', e, rawText);
      }
    }
    
    return {
      success: !!resData.success,
      message: resData.message || (resData.success ? 'Email dispatched successfully' : 'Failed to send email'),
      providerUsed: resData.providerUsed,
      attempts: resData.attempts,
      deliveryTimeMs: resData.deliveryTimeMs,
      messageId: resData.messageId,
      smtpResponse: resData.smtpResponse,
      serverResponse: resData.serverResponse,
      errorDetails: resData.errorDetails,
      debugMode: resData.debugMode,
      debugDetails: resData.debugDetails
    };
  } catch (err: any) {
    console.error('Email dispatch error:', err);
    return {
      success: false,
      message: err?.message || 'Error connecting to email service endpoint',
    };
  }
}

/**
 * Verify SMTP / Provider connection against server endpoint
 */
export async function verifySmtpConnection(
  provider: 'zoho' | 'gmail_oauth' | 'brevo' | 'smtp',
  configOverride?: EmailConfig
): Promise<{
  success: boolean;
  message: string;
  provider: string;
  latencyMs?: number;
  responseCode?: string;
  errorCategory?: 'SUCCESS' | 'AUTH_FAILED' | 'TIMEOUT' | 'INVALID_PASSWORD' | 'SERVER_ERROR';
}> {
  try {
    const activeConfig = configOverride || cachedEmailConfig || (await fetchEmailConfigFromFirestore());
    const token = await auth.currentUser?.getIdToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch('/api/email/verify-connection', {
      method: 'POST',
      credentials: 'include',
      headers,
      signal: AbortSignal.timeout(12000),
      body: JSON.stringify({
        provider,
        config: activeConfig,
      }),
    });
    const rawText = await res.text();
    if (res.headers.get('content-type')?.includes('application/json') || rawText.trim().startsWith('{')) {
      try {
        return JSON.parse(rawText);
      } catch (e) {
        return {
          success: false,
          provider,
          message: `JSON parse error: ${rawText.slice(0, 100)}`,
          errorCategory: 'SERVER_ERROR'
        };
      }
    } else {
      const stripped = rawText.replace(/<[^>]*>?/gm, ' ').replace(/\s+/g, ' ').trim();
      return {
        success: false,
        provider,
        message: stripped.length > 5 ? stripped.slice(0, 200) : `Server HTTP Error (${res.status})`,
        errorCategory: 'SERVER_ERROR'
      };
    }
  } catch (err: any) {
    return {
      success: false,
      provider,
      message: err?.message || 'SMTP Timeout or network error while verifying connection.',
      errorCategory: 'TIMEOUT',
    };
  }
}

/**
 * Automated 5-minute background health check audit
 */
export async function runEmailHealthCheck(configOverride?: EmailConfig): Promise<{
  success: boolean;
  timestamp: string;
  providersHealth: Record<string, { ok: boolean; statusText: string; latencyMs?: number }>;
  dnsVerification: { spf: boolean; dkim: boolean; dmarc: boolean };
  failoverTriggered: boolean;
  activeProviderNow: string;
}> {
  const activeConfig = configOverride || cachedEmailConfig;
  try {
    const token = await auth.currentUser?.getIdToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch('/api/email/health-check', {
      method: 'POST',
      credentials: 'include',
      headers,
      signal: AbortSignal.timeout(15000),
      body: JSON.stringify({ config: activeConfig }),
    });
    const rawText = await res.text();
    if (res.headers.get('content-type')?.includes('application/json') || rawText.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(rawText);
        if (parsed && parsed.providersHealth) {
          if (parsed.authoritativeConfig && typeof parsed.authoritativeConfig === 'object') {
            const merged = { ...DEFAULT_EMAIL_CONFIG, ...parsed.authoritativeConfig };
            cachedEmailConfig = merged;
            try {
              if (typeof window !== 'undefined') {
                localStorage.setItem('rh_email_config', JSON.stringify(merged));
              }
            } catch (e) {}
          }
          return parsed;
        }
        if (res.status === 401 || parsed?.message?.includes('Unauthorized')) {
          return {
            success: false,
            timestamp: new Date().toISOString(),
            providersHealth: {
              zoho: { ok: false, statusText: 'Admin Login Required' },
              gmail_oauth: { ok: false, statusText: 'Admin Login Required' },
              brevo: { ok: false, statusText: 'Admin Login Required' },
              smtp: { ok: false, statusText: 'Admin Login Required' },
            },
            dnsVerification: { spf: true, dkim: true, dmarc: true },
            failoverTriggered: false,
            activeProviderNow: activeConfig?.activeProvider || 'zoho',
          };
        }
      } catch (e) {
        // Fallback
      }
    }
    return {
      success: false,
      timestamp: new Date().toISOString(),
      providersHealth: {
        zoho: { ok: false, statusText: 'Endpoint non-JSON' },
        gmail_oauth: { ok: false, statusText: 'Endpoint non-JSON' },
        brevo: { ok: false, statusText: 'Endpoint non-JSON' },
        smtp: { ok: false, statusText: 'Endpoint non-JSON' },
      },
      dnsVerification: { spf: true, dkim: true, dmarc: true },
      failoverTriggered: false,
      activeProviderNow: activeConfig?.activeProvider || 'zoho',
    };
  } catch (err: any) {
    return {
      success: false,
      timestamp: new Date().toISOString(),
      providersHealth: {
        zoho: { ok: false, statusText: 'Health check timeout' },
        gmail_oauth: { ok: false, statusText: 'Health check timeout' },
        brevo: { ok: false, statusText: 'Health check timeout' },
        smtp: { ok: false, statusText: 'Health check timeout' },
      },
      dnsVerification: { spf: true, dkim: true, dmarc: true },
      failoverTriggered: false,
      activeProviderNow: activeConfig?.activeProvider || 'zoho',
    };
  }
}

/**
 * Test Email Connection helper
 */
export async function sendTestEmail(
  recipientEmail: string,
  configOverride?: EmailConfig
): Promise<{ success: boolean; message: string }> {
  const activeConfig = configOverride || cachedEmailConfig;
  return await sendEmailNotification({
    to: recipientEmail,
    subject: `🧪 RH Editography Test Email (${activeConfig.activeProvider.toUpperCase()})`,
    type: 'CUSTOM',
    text: `Hello! This is a test email sent from RH Editography to verify your ${activeConfig.activeProvider.toUpperCase()} email integration setup. All systems operational!`,
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #0f172a; color: #ffffff; padding: 24px; border-radius: 16px; border: 1px solid #1e293b;">
        <h2 style="color: #25d366; margin-top: 0;">RH Editography - Email Configuration Test</h2>
        <p>Your <strong>${activeConfig.activeProvider.toUpperCase()}</strong> email setup is working correctly!</p>
        <div style="background: rgba(255,255,255,0.05); padding: 16px; border-radius: 8px; font-family: monospace; font-size: 13px; color: #cbd5e1;">
          Provider: ${activeConfig.activeProvider}<br/>
          Recipient: ${recipientEmail}<br/>
          Timestamp: ${new Date().toLocaleString()}
        </div>
        <p style="color: #94a3b8; font-size: 12px; margin-top: 20px;">
          Sent automatically from RH Editography Admin Panel.
        </p>
      </div>
    `,
    configOverride: activeConfig,
  });
}
